import {useHttpClient} from '@raison/network-facade';
import {invariant, isKeyOf} from '@raison/toolbox';
import {Outcome} from '@raison/types';
import {type} from 'arktype';
import BigNumber from 'bignumber.js';
import {absurd} from 'fp-ts/lib/function';
import React, {FormEvent, useMemo} from 'react';
import {NavigateOptions} from 'react-router';
import {compose} from 'redux';
import {CountryPicker, getCountrySync} from '~/entities/countries';
import {useCurrencyFormat, usePrimaryCurrencyFormat, useRateToPrimaryCurrency} from '~/entities/currencies';
import {OperationDetail} from '~/entities/operations-fiat';
import {useLogout} from '~/entities/user';
import {
    connectFieldWith,
    connectSubmitToImmerForm,
    fieldBlurRecipe,
    fieldsCompleteRecipe,
    FormState,
    ImmerSetState,
    Issues,
    userInputRecipe,
    writeUserInput,
} from '~/library/form-models.v1';
import {RootLayout, RsnButton, RsnDialog, RsnInput, RsnRadioGroup, RsnSelect, useBoolean} from '~/library/ui-kit';
import {SendTravelRuleReviewIssues, submitTravelRuleReview} from '../api/submitTravelRuleReview';
import {useInHistoryStateNamespace} from '../lib/useInHistoryState';
import {NavigationEntryPoint, useNavigateFromEntry} from '../lib/useNavigateWithCount';
import {SendTxResources} from '../model/SendTxContainer';
import {useSendTxResources} from '../model/SendTxResourcesContext';
import {
    getParticipantFieldsFromUser,
    participantIsCompany,
    TrTxParticipantValues,
    TxParticipantType,
    validateTrTxParticipant,
    WalletType,
} from '../model/TrTxParticipant';
import {useErrorHandler} from '../model/useErrorHandler';
import {UserInfoResource} from '../model/useUserInfoResource';
import {VaspPicker} from '../model/VaspPicker';
import {FeeCalculationType, SendTxIntroResult, SendTxIntroValues} from './SendTxIntro';
import {AcknowledgmentDialog} from './views/AcknowledgmentDialog';
import {
    CompanyIdInfoDialog,
    Disclosure,
    TransactionSentForReview,
    WalletTypeInfoDialog,
    WalletVASPInfoDialog,
} from './views/SendTrTx.views';

type SendTrTxActionCallbacks = {
    fieldBlur: (field: keyof Values) => void;
    userInput: (updatedFields: Partial<Values>) => void;
    beneficiaryEntityChanged: (value: TxParticipantType, user: UserInfoResource) => void;
    openDialog: (dialogName: DialogName) => void;
    closeDialog: (dialogName?: DialogName) => void;
    clickedContinue: () => void;
    clickedSend: () => void;
    clickedConfirmOnRecipt: () => void;
    completeFields: () => void;
};

function SendTrTxStep2View({
    formState,
    amount,
    resources,
    intro,
    actions: act,
    currentDialog,
    canSubmit,
}: {
    formState: SendTxTravelRuleForm;
    amount: BigNumber;
    resources: SendTxResources;
    intro: SendTxIntroResult;
    actions: SendTrTxActionCallbacks;
    currentDialog: DialogName | undefined;
    canSubmit: boolean;
}) {
    const {values, hasIssues, hasServerIssues, ...form} = validateTrTxParticipant(formState);
    const cantSubmit = hasIssues || hasServerIssues;

    const connectField = connectFieldWith(form, act.fieldBlur);
    const connectPicker = (itsName: DialogName) => ({
        opened: itsName === currentDialog,
        onDropdownToggle: (opened: boolean) => {
            if (opened) {
                act.openDialog(itsName);
            } else {
                act.closeDialog(itsName);
            }
        },
    });

    const beneficiaryIsCompany = participantIsCompany(values, resources.user);
    const {format: formatAsSubject} = useCurrencyFormat(resources.asset);

    const t = {
        ThirdPartyIndividual: 'Third-party Individual',
        ThirdPartyCompany: 'Third-party Company',
        MyOwnWallet: 'Myself',
        ImSendingTo: "Who I'm sending to",
        BeneficiaryEntity: 'Beneficiary entity',
        Company: 'Company',
        CompanyID: 'Company ID',
        BeneficiaryName: beneficiaryIsCompany ? 'Company name' : 'Full name',
        BeneficiaryNameExample: beneficiaryIsCompany ? 'General Electric' : 'John Doe',
        BeneficiaryCountry: 'Country',
        BeneficiaryCity: 'City',
        BeneficiaryCityExample: 'Tallinn',
        BeneficiaryAddress: 'Address',
        BeneficiaryAddressExample: 'Roosikrantsi 11',
        VASP: 'VASP',
        Unhosted: 'Unhosted',
        WalletType: 'Wallet type',
        WalletVASP: 'Wallet VASP',
        Send: 'Send',
        AckTitle: `Sending ${formatAsSubject(amount)}`,
        AckConsent: `By submitting this form, you acknowledge that your personal data (including name, address, and identification information) will be shared with the other provider involved in this transfer, as required under the Proceeds of Crime (Money Laundering) and Terrorist Financing Act (PCMLTFA) and associated Regulations for electronic funds transfers (EFT) and virtual currency (VC) transfers.`,
    };

    const isSelfSelected = values.participantType === TxParticipantType.self;

    const handleSubmit = (ev: FormEvent<HTMLFormElement>) => {
        ev.preventDefault();

        if (cantSubmit) {
            act.completeFields();
            return;
        }

        act.clickedSend();
    };

    return (
        <form className='contents' onSubmit={handleSubmit}>
            <RsnRadioGroup
                layout='column'
                label={t.ImSendingTo}
                value={values.participantType}
                options={[
                    {label: t.ThirdPartyIndividual, value: TxParticipantType.individual},
                    {label: t.ThirdPartyCompany, value: TxParticipantType.company},
                    {label: t.MyOwnWallet, value: TxParticipantType.self},
                ]}
                onChange={value => act.beneficiaryEntityChanged(value, resources.user)}
            />

            <RsnInput
                label={
                    <RsnInput.Label subtext={`Example: ${t.BeneficiaryNameExample}`}>
                        {t.BeneficiaryName}
                    </RsnInput.Label>
                }
                value={values.participantName}
                onChange={ev => act.userInput({participantName: ev.target.value})}
                disabled={isSelfSelected}
                {...connectField('participantName')}
            />

            <CountryPicker
                format={'alpha3'}
                label={t.BeneficiaryCountry}
                placeholder='Select country'
                value={values.participantCountry}
                onChange={value => act.userInput({participantCountry: value})}
                disabled={isSelfSelected}
                {...connectPicker(DialogName.participantCountry)}
                {...connectField('participantCountry')}
            />

            <RsnInput
                label={
                    <RsnInput.Label subtext={`Example: ${t.BeneficiaryCityExample}`}>
                        {t.BeneficiaryCity}
                    </RsnInput.Label>
                }
                value={values.participantTown}
                onChange={ev => act.userInput({participantTown: ev.target.value})}
                disabled={isSelfSelected}
                {...connectField('participantTown')}
            />

            <RsnInput
                label={
                    <RsnInput.Label subtext={`Example: ${t.BeneficiaryAddressExample}`}>
                        {t.BeneficiaryAddress}
                    </RsnInput.Label>
                }
                value={values.participantAddress}
                onChange={ev => act.userInput({participantAddress: ev.target.value})}
                disabled={isSelfSelected}
                {...connectField('participantAddress')}
            />

            <RsnRadioGroup
                label={
                    <RsnInput.LabelWithInfo onInfoClick={() => act.openDialog(DialogName.walletType)}>
                        {t.WalletType}
                    </RsnInput.LabelWithInfo>
                }
                value={values.walletType}
                options={[
                    {value: 'vasp', label: t.VASP},
                    {value: 'unhosted', label: t.Unhosted},
                ]}
                onChange={value => act.userInput({walletType: value})}
            />

            <Disclosure open={values.walletType === 'vasp'}>
                <VaspPicker
                    label={
                        <RsnSelect.LabelWithInfo onInfoClick={() => act.openDialog(DialogName.vaspInfo)}>
                            {t.WalletVASP}
                        </RsnSelect.LabelWithInfo>
                    }
                    value={values.walletVasp}
                    onChange={value => act.userInput({walletVasp: value})}
                    {...connectPicker(DialogName.walletVasp)}
                    {...connectField('walletVasp')}
                />
            </Disclosure>

            <div className='mt-auto'>
                <RsnButton.Primary type='submit' disabled={!canSubmit}>
                    {t.Send}
                </RsnButton.Primary>
            </div>

            <RsnDialog.Controller onClose={() => act.closeDialog()}>
                {(() => {
                    switch (currentDialog) {
                        case DialogName.walletType:
                            return <WalletTypeInfoDialog />;
                        case DialogName.vaspInfo:
                            return <WalletVASPInfoDialog />;
                        case DialogName.companyId:
                            return <CompanyIdInfoDialog />;
                        case DialogName.ack:
                            return (
                                <AcknowledgmentDialog
                                    title={t.AckTitle}
                                    consent={t.AckConsent}
                                    onContinue={act.clickedContinue}
                                />
                            );
                        case DialogName.recipt:
                            return (
                                <ReciptDialog
                                    values={values}
                                    intro={intro}
                                    canConfirm={canSubmit}
                                    onConfirm={act.clickedConfirmOnRecipt}
                                />
                            );
                    }
                })()}
            </RsnDialog.Controller>
        </form>
    );
}

type SendOpTravelRuleIssues = Issues<SendTxTravelRuleValues & SendTxIntroValues>;

type Values = SendTxTravelRuleValues;
export type SendTxTravelRuleValues = SendTxTravelRuleForm['values'];

export type SendTxTravelRuleForm = ReturnType<typeof SendTxTravelRuleForm>;
export const SendTxTravelRuleForm = () =>
    FormState({
        ...TrTxParticipantValues(),
    });

type PickerName = (typeof PickerName)[keyof typeof PickerName];
const PickerName = {
    participantCountry: 'participantCountry',
    walletVasp: 'walletVasp',
} satisfies {
    [K in keyof Values]?: K;
};

type DialogName = (typeof DialogName)[keyof typeof DialogName];
const DialogName = {
    ...PickerName,
    walletType: 'walletType',
    vaspInfo: 'vaspInfo',
    companyId: 'lei',
    ack: 'ack',
    recipt: 'recipt',
} as const;

const SendTrTxDirection = type.never
    .or({
        view: "'beneficiary'",
        dialog: type.enumerated(...Object.values(DialogName)).optional(),
    })
    .or({
        view: "'finish'",
        beneficiaryType: type.enumerated(...Object.values(TxParticipantType)),
        dialog: 'undefined?',
    });

export function SendTrTxDirector({
    resources,
    intro,
    entry,
    onSubmitProblem,
    setFormState,
    formState,
}: {
    formState: SendTxTravelRuleForm;
    entry: NavigationEntryPoint;
    setFormState: ImmerSetState<SendTxTravelRuleForm>;
    intro: SendTxIntroResult;
    resources: SendTxResources;
    onSubmitProblem: (issues: Issues<SendTxIntroValues>) => void;
}) {
    const handleError = useErrorHandler();
    const http = useHttpClient();
    const logout = useLogout();

    const navigate = useNavigateFromEntry(entry);
    const navigateBack = () => navigate(-1);
    const useInHistoryState = useInHistoryStateNamespace('send_tr_flow', navigate);
    const [direction, setDirection] = useInHistoryState(SendTrTxDirection, {
        view: 'beneficiary',
    });

    const [isSending, setIsSending] = useBoolean(false);

    if (direction.view === 'finish') {
        return <TransactionSentForReview />;
    }

    function openDialog(dialogName: DialogName, options?: NavigateOptions) {
        setDirection(current => (current.view !== 'beneficiary' ? current : {...current, dialog: dialogName}), options);
    }

    function closeDialog() {
        if (direction.view === 'beneficiary' && direction.dialog) {
            navigate(-1);
        }
    }

    const connectSubmit = connectSubmitToImmerForm(setFormState);

    const sendForm = connectSubmit(async () => {
        const {id: accountId, type: accountType} = resources.asset;
        const values = {...formState.values, ...intro};

        const amount = intro.amountToBeReceived;

        setIsSending.on();
        const response = await submitTravelRuleReview(http, accountId, accountType, {...values, amount})
            .catch(crash => {
                gotSubmitOutcome({crash});
                return handleError(crash);
            })
            .finally(() => {
                setIsSending.off();
            });

        switch (response.status) {
            case 200:
            case 201:
                gotSubmitOutcome({value: true});
                return;
            case 400:
                gotSubmitOutcome({problem: response.json});
                return;
            case 401:
                logout();
                return;
            default:
                absurd(response);
        }

        function gotSubmitOutcome({value: success, problem: issues}: Outcome<true, SendTravelRuleReviewIssues>) {
            if (success) {
                setDirection({view: 'finish', beneficiaryType: formState.values.participantType});
            }
            if (issues) {
                setFormState(formState => {
                    formState.serverIssues = interpretServerIssues(issues);
                });
            }
            if (issues && (['address', 'comment', 'amount'] as const).some(field => issues[field])) {
                if (direction.dialog) {
                    navigate(-2);
                } else {
                    navigate(-1);
                }
                onSubmitProblem(interpretServerIssues(issues));
            }
        }
    });

    const actions: SendTrTxActionCallbacks = {
        fieldBlur: compose(setFormState, fieldBlurRecipe),
        userInput: compose(setFormState, userInputRecipe),
        beneficiaryEntityChanged: (participantType, user) => {
            setFormState(formState => {
                writeUserInput(formState, {participantType});
                if (participantType === TxParticipantType.self) {
                    writeUserInput(formState, getParticipantFieldsFromUser(user));
                }
            });
        },
        openDialog: openDialog,
        closeDialog: currentDialog => {
            closeDialog();
            setFormState(formState => {
                const {flags} = formState;
                if (isKeyOf(PickerName, currentDialog) && !flags[currentDialog]?.completed) {
                    flags[currentDialog] = {...flags[currentDialog], completed: true};
                }
            });
        },
        clickedContinue: () => {
            sendForm();
            setDirection(current => ({...current, dialog: undefined}));
        },
        clickedSend: () => {
            openDialog(DialogName.recipt);
        },
        clickedConfirmOnRecipt: () => {
            if (formState.values.walletType === 'vasp') {
                openDialog(DialogName.ack, {replace: true});
            } else {
                sendForm();
            }
        },
        completeFields: () => {
            setFormState(fieldsCompleteRecipe(...(Object.keys(formState.flags) as (keyof Values)[])));
        },
    };

    return (
        <RootLayout common title={`Send ${resources.asset.code}`} navigateBack={navigateBack}>
            <SendTrTxStep2View
                resources={resources}
                intro={intro}
                formState={formState}
                amount={intro.amountToBeReceived}
                currentDialog={direction.dialog}
                actions={actions}
                canSubmit={!isSending}
            />
        </RootLayout>
    );
}

function ReciptDialog({
    values,
    intro,
    canConfirm,
    onConfirm,
}: {
    values: Values;
    intro: SendTxIntroResult;
    canConfirm: boolean;
    onConfirm: () => void;
}) {
    const t = {
        ConfirmDetails: 'Confirm details',
        FromAccount: 'From account',
        ToAddress: 'To address',
        Comment: 'Details',
        FullName: 'Full name',
        WhoImSendingTo: 'Who I’m sending to',
        BeneficiaryCountry: 'Beneficiary country',
        WalletType: 'Wallet type',
        WalletVASP: 'Wallet VASP',
        Confirm: 'Confirm',
        TxParticipantType: {
            third_party_individual: 'Third-party Individual',
            third_party_company: 'Third-party Company',
            own_wallet: 'Myself',
        } satisfies {[K in TxParticipantType]: string},
        WalletTypeName: {
            vasp: 'VASP',
            unhosted: 'Unhosted',
        } satisfies {[K in WalletType]: string},

        TransactionFee: 'Transaction fee',
        FeeHandling: 'Fee handling',
        FeeHandlingName: {
            included: 'Included',
            on_top: 'On top',
        } satisfies {[K in FeeCalculationType]: string},
        YouSpend: 'You spend',
        ApproxValue: 'Approx value',
        BeneficiaryGets: 'Beneficiary gets',
    };

    const {asset} = useSendTxResources();

    const {format: formatAsSubject} = useCurrencyFormat(asset);
    const {format: formatAsSubjectBlockchain} = useCurrencyFormat(asset.blockchain);
    const rate = useRateToPrimaryCurrency(asset);
    const {format: formatAsPrimary} = usePrimaryCurrencyFormat();

    const countryObject = useMemo(() => {
        // How to remove these invariants: make Country object the value of the form. Use form model that supports
        // typing InitialValues and ValidValues, this popup will have to accept ValidValues
        const countryCode = values.participantCountry;
        invariant(countryCode !== null, 'Validation must ensure participantCountry is not null');
        const countryObject = countryCode && getCountrySync(countryCode);
        invariant(countryObject !== undefined, 'Country object must be found if CountryPicker uses same list');
        return countryObject;
    }, [values.participantCountry]);

    const isOnOwnBlockchain = asset.blockchain.currencyCode === asset.code;

    return (
        <RsnDialog.Window>
            <RsnDialog.CenteredHeader>{t.ConfirmDetails}</RsnDialog.CenteredHeader>
            <RsnDialog.Body align={'left'}>
                <div className='flex flex-col gap-4 rounded-[10px] bg-neutral-20 p-5'>
                    <OperationDetail.Layout>
                        <OperationDetail label={t.FromAccount}>{asset.name}</OperationDetail>
                        <OperationDetail label={t.ToAddress}>
                            <span className='break-all'>{intro.address}</span>
                        </OperationDetail>
                        <OperationDetail label={t.Comment}>{intro.comment}</OperationDetail>
                    </OperationDetail.Layout>
                    <OperationDetail.Layout>
                        <OperationDetail label={t.WhoImSendingTo}>
                            {t.TxParticipantType[values.participantType]}
                        </OperationDetail>
                        <OperationDetail label={t.FullName}>{values.participantName}</OperationDetail>
                        <OperationDetail label={t.BeneficiaryCountry}>{countryObject.name}</OperationDetail>
                        <OperationDetail label={t.WalletType}>{t.WalletTypeName[values.walletType]}</OperationDetail>
                        {values.walletVasp && (
                            <OperationDetail label={t.WalletVASP}>{values.walletVasp.name}</OperationDetail>
                        )}
                    </OperationDetail.Layout>
                    <OperationDetail.Layout>
                        <OperationDetail label={t.FeeHandling}>
                            {t.FeeHandlingName[intro.feeCalculationType]}
                        </OperationDetail>
                        <OperationDetail label={t.TransactionFee}>
                            {formatAsSubjectBlockchain(intro.feeAmount)}
                        </OperationDetail>
                        {!isOnOwnBlockchain && (
                            <OperationDetail label={t.YouSpend}>
                                {formatAsSubject(intro.amountToBeSent)}
                            </OperationDetail>
                        )}
                        <OperationDetail label={t.ApproxValue}>
                            {formatAsPrimary(intro.amountToBeSent.multipliedBy(rate), {approx: true})}
                        </OperationDetail>
                        <OperationDetail label={t.BeneficiaryGets}>
                            {formatAsSubject(intro.amountToBeReceived)}
                        </OperationDetail>
                    </OperationDetail.Layout>
                </div>
            </RsnDialog.Body>
            <RsnDialog.Footer column>
                <RsnButton.Primary disabled={!canConfirm} onClick={onConfirm}>
                    {t.Confirm}
                </RsnButton.Primary>
            </RsnDialog.Footer>
        </RsnDialog.Window>
    );
}

function interpretServerIssues(issues: SendOpTravelRuleIssues | undefined) {
    issues = {...issues};
    if (issues.amount?.startsWith('Not enough crypto to pay blockchain fee')) {
        issues.amount = 'Not enough crypto to pay fee';
    }
    return issues;
}
